import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Globe, Smartphone, Zap, TrendingUp, CheckCircle2, ArrowRight,
  MousePointerClick, Shield, Sparkles, Code, Palette, Layout,
  FileText, Settings, Users, Layers, Search, Monitor, X, AlertTriangle, Check,
  ShoppingCart, Briefcase, Building2, Rocket,
  RefreshCw, Workflow, BarChart3, Eye, ExternalLink, Plug,
  Calendar, Mail, CreditCard, Bot, BookOpen, Server, PenTool
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import ServiceHero, { Highlight } from "./ServiceHero";
import pripolsaCover from "@/assets/clients/pripolsa-cover.webp";

import pripolsaLogo from "@/assets/clients/pripolsa.webp";
import darklandLogo from "@/assets/clients/darkland.webp";
import gonerstudioLogo from "@/assets/clients/gonerstudio.webp";
import timeworkLogo from "@/assets/clients/timework.webp";
import habitalismoLogo from "@/assets/clients/habitalisimo.webp";
import snowgranadaLogo from "@/assets/clients/snowgranada.webp";
import r8aLogo from "@/assets/clients/r8a.webp";

/* ───── SCROLL ANIMATION ───── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

function AnimatedNumber({ value, suffix = "" }: { value: number; suffix?: string }) {
  const { ref, isVisible } = useInView();
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const start = Date.now();
    const duration = 2000;
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(value * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isVisible, value]);
  return <span ref={ref}>{current}{suffix}</span>;
}

/* ───── PORTFOLIO DATA ───── */
const portfolio = [
  {
    name: "PRIPOLSA",
    type: "Web Corporativa",
    description: "Web corporativa para empresa de obras y servicios con catálogo de proyectos y sistema de presupuestos.",
    image: pripolsaCover,
    url: "https://pripolsa.es",
    tags: ["Corporativa", "Catálogo", "SEO"],
    logo: pripolsaLogo,
    logoBg: "bg-white",
  },
  {
    name: "Darkland Catalunya",
    type: "Landing + Ticketing",
    description: "Landing de alta conversión para experiencia de terror con pasarela de ticketing y campañas Meta/TikTok integradas.",
    image: null,
    url: "https://darkland.cat/",
    tags: ["Landing", "Ticketing", "Conversión"],
    logo: darklandLogo,
    logoBg: "bg-black",
  },
  {
    name: "Goner Studio",
    type: "Web Corporativa",
    description: "Web profesional para agencia de marketing con embudo automatizado y pasarela de suscripciones.",
    image: null,
    url: "https://gonerstudio.com",
    tags: ["Corporativa", "Suscripciones", "Embudos"],
    logo: gonerstudioLogo,
    logoBg: "bg-black",
  },
  {
    name: "Timework",
    type: "Landing SaaS",
    description: "Landing de alta conversión para software SaaS con suscripciones mensuales y sistema de demos.",
    image: null,
    url: "https://timework.es/",
    tags: ["SaaS", "Pagos", "Demos"],
    logo: timeworkLogo,
    logoBg: "bg-white",
  },
  {
    name: "R8A Reformas",
    type: "Landing Premium",
    description: "Landing especializada en captar proyectos de reformas de alto standing con presupuestos automáticos.",
    image: null,
    url: "https://roblesochoareforma.com/",
    tags: ["Landing", "Premium", "Leads"],
    logo: r8aLogo,
    logoBg: "bg-black",
  },
  {
    name: "Habitalisimo",
    type: "Web Inmobiliaria",
    description: "Web inmobiliaria con modelo de cuota fija, landing de conversión y calendario de visitas integrado.",
    image: null,
    url: "https://habitalisimo.com/",
    tags: ["Inmobiliaria", "Conversión", "Citas"],
    logo: habitalismoLogo,
    logoBg: "bg-black",
  },
  {
    name: "SnowGranada",
    type: "Web Vacacional",
    description: "Web de alquiler vacacional multilingüe con pasarela de pago y embudos automatizados.",
    image: null,
    url: "https://snowgranada.com/",
    tags: ["Vacacional", "Multilingüe", "Pagos"],
    logo: snowgranadaLogo,
    logoBg: "bg-blue-600",
  },
];

const faqs = [
  { q: "¿Qué tipo de webs diseñáis?", a: "Diseñamos todo tipo: corporativas, landing pages, tiendas online, blogs, páginas de servicios, webs para campañas publicitarias y más. Cada proyecto se adapta al objetivo del negocio." },
  { q: "¿Puedo modificar el contenido después?", a: "Sí. Podrás editar textos, añadir páginas y actualizar la información de tu negocio cuando lo necesites." },
  { q: "¿Incluye hosting y dominio?", a: "Sí. Gestionamos el alojamiento en nuestros servidores mientras tengas la suscripción activa. Si decides dejar el servicio, migramos la web sin restricciones." },
  { q: "¿Se integra con mi CRM y sistemas de pago?", a: "Sí. Las webs se integran con CRM, formularios de captación, automatizaciones, sistemas de pago y reservas." },
  { q: "¿Cuánto tarda el proceso?", a: "Depende del proyecto. Una landing page puede estar lista en 1-2 semanas. Una web corporativa completa, en 3-4 semanas." },
  { q: "¿Las webs son responsive?", a: "Sí. Todas las páginas se desarrollan para funcionar perfectamente en móvil, tablet y ordenador." },
  { q: "¿Incluye blog?", a: "Sí, si lo necesitas. Podemos integrar un blog para publicar contenido y mejorar tu posicionamiento en buscadores." },
  { q: "¿Hay permanencia?", a: "No. Diseñamos y desarrollamos tu web. Puedes contratar mantenimiento mensual si lo necesitas, pero sin permanencia." },
];

const WebsitesDemo = () => {
  return (
    <div className="space-y-32 overflow-hidden">

      {/* ━━━ HERO ━━━ */}
      <ServiceHero
        eyebrow="Websites & Landings"
        title={<>Webs que convierten <Highlight>visitantes en clientes</Highlight></>}
        description={<>Diseñamos y desarrollamos páginas web y landing pages profesionales que captan clientes, generan ventas y refuerzan la presencia digital de tu negocio.</>}
        includes={["100% Responsive", "SEO Optimizado", "Alta conversión", "Sin permanencia"]}
        primaryCta={{ label: "Solicitar presupuesto", href: "/#contact" }}
        secondaryCta={{ label: "Ver precios", href: "/#pricing" }}
      />

      {/* ━━━ PORTFOLIO ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">
              <Sparkles size={14} className="mr-1" />
              Nuestro trabajo
            </Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Webs que hemos creado</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Cada proyecto está diseñado para cumplir objetivos específicos del negocio.
            </p>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolio.map((project, idx) => (
            <AnimateIn key={idx} delay={idx * 0.08}>
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
              >
                <Card className="overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all h-full">
                  {/* Browser frame */}
                  <div className="bg-muted border-b border-border">
                    <div className="h-7 flex items-center px-3 gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                      <div className="flex-1 mx-3">
                        <div className="h-4 bg-background rounded flex items-center justify-center">
                          <span className="text-[10px] text-muted-foreground truncate">{project.url.replace("https://", "")}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Preview */}
                  <div className="aspect-video bg-muted/30 relative overflow-hidden">
                    {project.image ? (
                      <img src={project.image} alt={project.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5">
                        <div className="text-center">
                          {project.logo ? (
                            <div className={`w-16 h-16 rounded-xl ${project.logoBg} flex items-center justify-center mx-auto mb-2 shadow-md`}>
                              <img src={project.logo} alt={project.name} className="w-10 h-10 object-contain" />
                            </div>
                          ) : (
                            <Globe className="w-10 h-10 text-primary/30 mx-auto mb-2" />
                          )}
                        </div>
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="flex items-center gap-2 text-primary-foreground font-medium">
                        <ExternalLink size={18} />
                        Visitar web
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{project.name}</h4>
                      <Badge variant="outline" className="text-xs">{project.type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{project.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {project.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full bg-muted text-xs">{tag}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </a>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ IMPACT NUMBERS ━━━ */}
      <section>
        <AnimateIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: 47, suffix: "+", label: "Sitios lanzados", icon: <Globe className="w-5 h-5" /> },
              { value: 42, suffix: "%", label: "Conversión media", icon: <TrendingUp className="w-5 h-5" /> },
              { value: 15, suffix: "s", label: "Tiempo de carga medio", icon: <Zap className="w-5 h-5" /> },
              { value: 98, suffix: "%", label: "Satisfacción clientes", icon: <CheckCircle2 className="w-5 h-5" /> },
            ].map((stat, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-card border border-border text-center hover:border-primary/30 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-3">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-primary mb-1">
                  {stat.label === "Tiempo de carga medio" ? "<" : ""}<AnimatedNumber value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ FEATURES GRID ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">
              <Code size={14} className="mr-1" />
              Qué hacemos
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Todo lo que necesita tu web</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Cada proyecto incluye diseño, desarrollo, integraciones y optimización completa.
            </p>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: <Palette className="w-6 h-6" />,
              title: "Diseño personalizado",
              desc: "Webs diseñadas desde cero o con plantillas personalizadas, adaptadas a la identidad de tu marca.",
              items: ["Diseño desde cero o plantilla", "Identidad visual de tu marca", "Cambios hasta aprobación", "UX optimizada"],
            },
            {
              icon: <Layout className="w-6 h-6" />,
              title: "Todo tipo de páginas",
              desc: "Corporativas, landing pages de captación, páginas de venta, campañas publicitarias y blogs.",
              items: ["Webs corporativas", "Landing pages", "Páginas de venta", "Blogs y contenido"],
            },
            {
              icon: <MousePointerClick className="w-6 h-6" />,
              title: "Landings de conversión",
              desc: "Páginas diseñadas para campañas de marketing con formularios, CRM y tracking integrados.",
              items: ["Formularios de contacto", "Integración con CRM", "Botones de pago", "Tracking de conversiones"],
            },
            {
              icon: <Plug className="w-6 h-6" />,
              title: "Integraciones",
              desc: "Tu web conectada con CRM, formularios, automatizaciones, pagos y sistema de citas.",
              items: ["CRM y leads", "Automatizaciones", "Sistemas de pago", "Reservas y citas"],
            },
            {
              icon: <Smartphone className="w-6 h-6" />,
              title: "Responsive y optimizada",
              desc: "Diseño perfecto en móvil, tablet y desktop. Velocidad de carga optimizada.",
              items: ["Responsive en todos los dispositivos", "Velocidad <2s", "SEO técnico", "Código optimizado"],
            },
            {
              icon: <FileText className="w-6 h-6" />,
              title: "Gestión de contenido",
              desc: "Edita textos, añade páginas y actualiza la información de tu negocio sin depender de nadie.",
              items: ["Editar textos", "Añadir páginas", "Actualizar información", "Autonomía total"],
            },
          ].map((feature, idx) => (
            <AnimateIn key={idx} delay={idx * 0.06}>
              <Card className="p-6 h-full hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h4 className="font-semibold mb-2">{feature.title}</h4>
                <p className="text-sm text-muted-foreground mb-4">{feature.desc}</p>
                <div className="space-y-2">
                  {feature.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 size={14} className="text-primary shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ ADDITIONAL SERVICES ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Servicios adicionales incluidos</h3>
            <p className="text-muted-foreground">Todo lo que tu web necesita para funcionar al 100%</p>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: <BookOpen className="w-6 h-6" />,
              title: "Blog integrado",
              desc: "Sistema de blog para publicar artículos y contenido periódico. Facilita estrategias de contenido y posicionamiento en buscadores.",
            },
            {
              icon: <Server className="w-6 h-6" />,
              title: "Hosting y dominio",
              desc: "Alojamiento en nuestros servidores con mantenimiento incluido. Dominio propio o gestionado. Migración libre sin restricciones.",
            },
            {
              icon: <FileText className="w-6 h-6" />,
              title: "Gestión de contenido",
              desc: "Edita textos, añade páginas y actualiza la información de tu negocio cuando lo necesites, sin depender de nadie.",
            },
            {
              icon: <Search className="w-6 h-6" />,
              title: "SEO técnico",
              desc: "Estructura optimizada para buscadores: meta tags, sitemap, velocidad de carga y compatibilidad con Google.",
            },
            {
              icon: <Shield className="w-6 h-6" />,
              title: "Seguridad SSL",
              desc: "Certificado SSL incluido, código limpio y prácticas de seguridad web para proteger tu sitio y tus visitantes.",
            },
            {
              icon: <BarChart3 className="w-6 h-6" />,
              title: "Analytics integrado",
              desc: "Integración con herramientas de analítica para medir tráfico, conversiones y comportamiento de usuarios.",
            },
          ].map((service, idx) => (
            <AnimateIn key={idx} delay={idx * 0.06}>
              <Card className="p-6 h-full hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {service.icon}
                </div>
                <h4 className="font-semibold mb-2">{service.title}</h4>
                <p className="text-sm text-muted-foreground">{service.desc}</p>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ WHAT'S INCLUDED vs GUARANTEES ━━━ */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <CheckCircle2 size={18} className="text-chart-4" />
                Qué incluye cada proyecto
              </h4>
              <ul className="space-y-2">
                {[
                  "Diseño UX/UI personalizado",
                  "Desarrollo responsive completo",
                  "Optimización SEO técnico",
                  "Integración con analytics",
                  "Formularios de contacto/leads",
                  "Hosting y dominio gestionado",
                  "SSL y seguridad incluidos",
                  "Formación para gestión básica",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 size={14} className="text-chart-4 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-primary/5 border border-primary/20">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Shield size={18} className="text-primary" />
                Garantías
              </h4>
              <ul className="space-y-2">
                {[
                  "Entrega en plazo garantizada",
                  "Revisiones ilimitadas hasta aprobación",
                  "30 días de soporte post-lanzamiento",
                  "Score de velocidad mínimo 90/100",
                  "Compatible con todos los navegadores",
                  "Código limpio y documentado",
                  "Migración libre si dejas el servicio",
                  "Sin permanencia",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 size={14} className="text-primary shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ PROCESS ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Nuestro proceso</h3>
            <p className="text-muted-foreground">De la idea al lanzamiento en semanas, no meses</p>
          </div>
        </AnimateIn>

        {/* Desktop */}
        <div className="hidden md:grid grid-cols-5 gap-4">
          {[
            { step: "01", title: "Brief", desc: "Entendemos tus objetivos, público y necesidades del proyecto", icon: <FileText className="w-5 h-5" /> },
            { step: "02", title: "Wireframes", desc: "Diseñamos la estructura, navegación y flujos de usuario", icon: <Layout className="w-5 h-5" /> },
            { step: "03", title: "Diseño", desc: "Creamos el diseño visual completo adaptado a tu marca", icon: <Palette className="w-5 h-5" /> },
            { step: "04", title: "Desarrollo", desc: "Construimos la web funcional con todas las integraciones", icon: <Code className="w-5 h-5" /> },
            { step: "05", title: "Lanzamiento", desc: "Publicamos, optimizamos y formamos al equipo", icon: <Zap className="w-5 h-5" /> },
          ].map((item, idx) => (
            <AnimateIn key={idx} delay={idx * 0.1}>
              <div className="relative p-5 rounded-xl bg-card border border-border text-center hover:border-primary/30 transition-all">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-3">
                  {item.icon}
                </div>
                <div className="text-xs font-bold text-primary/40 mb-1">FASE {item.step}</div>
                <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
                {idx < 4 && (
                  <div className="absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                    <ArrowRight size={14} className="text-muted-foreground" />
                  </div>
                )}
              </div>
            </AnimateIn>
          ))}
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-3">
          {[
            { step: "01", title: "Brief", desc: "Entendemos tus objetivos y necesidades", icon: <FileText className="w-5 h-5" /> },
            { step: "02", title: "Wireframes", desc: "Estructura y flujos de usuario", icon: <Layout className="w-5 h-5" /> },
            { step: "03", title: "Diseño", desc: "Diseño visual de tu marca", icon: <Palette className="w-5 h-5" /> },
            { step: "04", title: "Desarrollo", desc: "Web funcional con integraciones", icon: <Code className="w-5 h-5" /> },
            { step: "05", title: "Lanzamiento", desc: "Publicación y optimización", icon: <Zap className="w-5 h-5" /> },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                {item.icon}
              </div>
              <div>
                <div className="text-xs font-bold text-primary/40">FASE {item.step}</div>
                <h4 className="font-semibold text-sm">{item.title}</h4>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ━━━ TECH STACK ━━━ */}
      <section>
        <AnimateIn>
          <Card className="p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Code size={20} className="text-primary" />
              Tecnologías que utilizamos
            </h3>
            <div className="flex flex-wrap gap-3">
              {["React / Next.js", "WordPress", "Webflow", "Shopify", "WooCommerce", "Stripe", "Tailwind CSS", "TypeScript"].map((tech, idx) => (
                <span key={idx} className="px-4 py-2 rounded-lg bg-muted font-medium text-sm">{tech}</span>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Elegimos la tecnología óptima para cada proyecto según tus necesidades, presupuesto y escalabilidad.
            </p>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ ¿PARA QUIÉN ES? ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">¿Es para ti?</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">¿Para quién es este servicio?</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Cualquier negocio que necesite una presencia digital profesional que genere resultados.
            </p>
          </div>
        </AnimateIn>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          <AnimateIn delay={0.1}>
            <Card className="h-full border-chart-4/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-chart-4/10 flex items-center justify-center">
                    <Check className="w-5 h-5 text-chart-4" />
                  </div>
                  <h4 className="font-bold text-lg">Ideal para</h4>
                </div>
                <div className="space-y-4">
                  {[
                    { icon: <Building2 className="w-4 h-4" />, title: "Empresas sin web", desc: "Negocios que necesitan crear su presencia digital desde cero." },
                    { icon: <Rocket className="w-4 h-4" />, title: "Lanzamientos y campañas", desc: "Landing pages para lanzar productos, ofertas o eventos." },
                    { icon: <ShoppingCart className="w-4 h-4" />, title: "E-commerce", desc: "Tiendas online que necesitan vender productos o servicios." },
                    { icon: <Briefcase className="w-4 h-4" />, title: "Profesionales y servicios", desc: "Clínicas, despachos, consultoras, formación…" },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-chart-4/10 flex items-center justify-center text-chart-4 shrink-0 mt-0.5">
                        {item.icon}
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">{item.title}</h5>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </AnimateIn>

          <AnimateIn delay={0.2}>
            <Card className="h-full border-destructive/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                    <X className="w-5 h-5 text-destructive" />
                  </div>
                  <h4 className="font-bold text-lg">No es para ti si…</h4>
                </div>
                <div className="space-y-4">
                  {[
                    "Solo necesitas una web gratuita sin diseño profesional ni estrategia de conversión.",
                    "No tienes claro qué servicios o productos ofreces todavía.",
                    "Buscas solo un blog personal sin objetivos comerciales.",
                    "No estás dispuesto a invertir en una herramienta de captación real.",
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0 mt-0.5">
                        <AlertTriangle className="w-4 h-4 text-destructive" />
                      </div>
                      <p className="text-sm text-muted-foreground">{text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </AnimateIn>
        </div>
      </section>

      {/* ━━━ ¿POR QUÉ CON VIBRA? ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Comparativa</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">¿Por qué con Vibra?</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Diferencias reales entre hacer tu web por tu cuenta o con un equipo especializado.
            </p>
          </div>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-3 border-b border-border">
                  <div className="p-4 md:p-6" />
                  <div className="p-4 md:p-6 text-center border-x border-border bg-muted/30">
                    <span className="text-sm font-semibold text-muted-foreground">Hacerlo tú</span>
                  </div>
                  <div className="p-4 md:p-6 text-center bg-primary/5">
                    <span className="text-sm font-bold text-primary">Con Vibra</span>
                  </div>
                </div>
                {[
                  { feature: "Diseño profesional personalizado", solo: false, vibra: true },
                  { feature: "Optimización SEO técnica", solo: false, vibra: true },
                  { feature: "Velocidad de carga <2s", solo: false, vibra: true },
                  { feature: "Integración con CRM y automatizaciones", solo: false, vibra: true },
                  { feature: "Responsive en todos los dispositivos", solo: false, vibra: true },
                  { feature: "Soporte y mantenimiento continuo", solo: false, vibra: true },
                  { feature: "Tiempo dedicado por tu parte", solo: true, vibra: false },
                  { feature: "Curva de aprendizaje técnica", solo: true, vibra: false },
                ].map((row, idx) => (
                  <div key={idx} className={`grid grid-cols-3 border-b border-border last:border-0 ${idx % 2 === 0 ? "" : "bg-muted/10"}`}>
                    <div className="p-4 md:p-5 flex items-center">
                      <span className="text-sm font-medium">{row.feature}</span>
                    </div>
                    <div className="p-4 md:p-5 flex items-center justify-center border-x border-border">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${row.solo ? "bg-destructive/10" : "bg-destructive/10"}`}>
                        <X size={14} className="text-destructive" />
                      </div>
                    </div>
                    <div className="p-4 md:p-5 flex items-center justify-center bg-primary/5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${row.vibra ? "bg-chart-4/10" : "bg-chart-4/10"}`}>
                        <Check size={14} className="text-chart-4" />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ FAQs ━━━ */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Preguntas frecuentes</h3>
          </div>
        </AnimateIn>
        <AnimateIn>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-sm font-medium hover:no-underline">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ CTA ━━━ */}
      <section>
        <AnimateIn>
          <div className="relative p-10 md:p-16 rounded-3xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border border-primary/20 text-center overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
            <h3 className="text-2xl md:text-4xl font-bold mb-4 relative">¿Listo para tu nueva web?</h3>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto relative">
              Cuéntanos tu proyecto y te preparamos una propuesta personalizada. Sin compromiso, sin permanencia.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative">
              <Link to="/#contact">
                <Button size="lg" className="gap-2 text-base px-8 shadow-glow">
                  Solicitar presupuesto
                  <ArrowRight size={16} />
                </Button>
              </Link>
              <Link to="/#pricing">
                <Button size="lg" variant="outline" className="gap-2 text-base px-8">
                  Ver precios
                </Button>
              </Link>
            </div>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default WebsitesDemo;
